import { FC, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { twJoin } from 'tailwind-merge';

import { zParsedSelectorString } from '@dbt-labs/selector';
import { Link } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDiscoveryAutocompleteSelector } from '@/hooks/discovery/autocompleteSelector';
import { BoldSearchHighlight } from '@/pages/account/project/search/components/BoldedText';
import { unique } from '@/util/array';
import { mod } from '@/util/math';

interface SelectorHintProps {
  fillText: (text: string) => void;
  children: string;
  description?: string | null;
  isSupported?: boolean;
  isSelected: boolean;
  toBold: string | null;
}

const SelectorHint: FC<SelectorHintProps> = ({
  fillText,
  description,
  isSupported = true,
  toBold,
  isSelected,
  children,
}) => {
  return (
    <li
      className={twJoin(
        'w-full list-none overflow-hidden truncate text-fgMain focus-within:border-transparent focus-within:bg-bgMainHover hover:bg-bgMainHover',
        isSelected ? 'bg-bgBrandMuted' : '',
      )}
    >
      <button
        disabled={!isSupported}
        className="flex w-full cursor-pointer px-4 py-1 text-left focus:outline-none"
        role="option"
        aria-selected={isSelected}
        onClick={() => {
          fillText(children);
        }}
      >
        <span className="flex-0">
          {toBold ? (
            <BoldSearchHighlight
              text={children}
              query={toBold}
              boldProps={{ className: 'bg-bgBrandMuted' }}
            />
          ) : (
            children
          )}
        </span>
        {description && (
          <span className="flex-1 text-right italic text-fgAlt">{description}</span>
        )}
        {!isSupported && (
          <span className="italic text-fgDecorative">
            <span className="line-through">{children}</span> Selector not supported
          </span>
        )}
      </button>
    </li>
  );
};

interface SelectorTooltipProps {
  className?: string;
  /** If false, only display when we've already matched a method */
  shouldShowBeforeMatch?: boolean;
  textInput: string | null;
  updateSelector: (selector: string) => void;
  show: boolean;
  selectedIndex: number | undefined;
  setSelectedAutocomplete: (autocompletion: string | null) => void;
}

const MAX_RESULTS = 5;

const useAutocompleteResults = (textInput: string | null) => {
  const { environmentId } = useDbtCloudProject();
  const [selector, setSelector] = useState<string | null>(null);

  useDebounce(
    () => {
      setSelector(textInput);
    },
    400,
    [textInput],
  );

  const [autocompleteQuery] = useDiscoveryAutocompleteSelector({
    environmentId,
    selector,
  });
  const results = autocompleteQuery.data?.environment?.selectorAutocomplete;
  if (results?.length) {
    // eslint-disable-next-line react-compiler/react-compiler
    results.length = Math.min(results.length, MAX_RESULTS);
  }
  return results;
};

/**
 * Index can be negative, and modular arithmetic doesn't work well
 * with negative numbers in javascript.
 */
const normalizeSelectedIndex = (
  index: number | undefined,
  results: any[] | undefined | null,
) => {
  if (index == null || !results?.length) return index;
  return mod(index, results.length);
};

const SelectorTooltipDisplay: FC<SelectorTooltipProps> = ({
  className,
  textInput,
  updateSelector,
  selectedIndex,
  setSelectedAutocomplete,
}) => {
  const [autocompleteInput, setAutocompleteInput] = useState(textInput);
  useEffect(() => {
    if (selectedIndex != null) return;
    setAutocompleteInput(textInput);
  }, [selectedIndex, textInput]);

  const results = useAutocompleteResults(autocompleteInput);
  selectedIndex = normalizeSelectedIndex(selectedIndex, results);

  useEffect(() => {
    if (selectedIndex == null || !results?.length) return;
    const selectedAutocomplete = results[selectedIndex];
    if (!selectedAutocomplete?.completion) return;
    setSelectedAutocomplete(selectedAutocomplete.completion);
  }, [results, selectedIndex, setSelectedAutocomplete]);

  const selector = useMemo(() => {
    const selector = zParsedSelectorString.safeParse(autocompleteInput ?? '');
    if (!selector.success) return null;
    const lastSelectorGroup = selector.data[selector.data.length - 1];
    const lastSelector = lastSelectorGroup[lastSelectorGroup.length - 1];
    return lastSelector;
  }, [autocompleteInput]);

  return results?.length ? (
    <div className={className}>
      <div
        className="scrolling-auto space-y-2 overflow-x-hidden rounded-lg border border-borderMuted bg-bgMain text-sm shadow-hover"
        role="menu"
        id="searchMenu"
        aria-labelledby="searchMenuTitle"
      >
        <div id="searchMenuTitle" className="px-4 pt-2 text-fgDecorative">
          Suggested Selectors
        </div>
        <ol>
          {results.filter(unique).map((match, index) => {
            if (!match) return null;
            const key = `${match.completion}-${match.type}`;
            const isSelected = index === selectedIndex;
            return (
              <SelectorHint
                key={key}
                fillText={() => {
                  updateSelector(match.completion);
                }}
                description={match.description}
                toBold={selector?.value ?? null}
                isSelected={isSelected}
              >
                {match.display}
              </SelectorHint>
            );
          })}
        </ol>
        <div className="border-t border-borderMuted bg-bgBackground px-4 py-1.5">
          <span className="text-fgMain">Learn more about </span>
          <Link
            className="inline-block"
            shouldOpenNewTab
            isInternal={false}
            to="https://docs.getdbt.com/reference/node-selection/syntax"
          >
            selector syntax
          </Link>
        </div>
      </div>
    </div>
  ) : null;
};

export const SelectorTooltip: FC<SelectorTooltipProps> = (props) => {
  if (!props.show) return null;
  return <SelectorTooltipDisplay {...props} />;
};
